import type { ElementSchemaType, RichTextFontSchemaType } from '@vedalib/editor/lib/brand'
import type { ElementValue } from '@vedalib/editor/lib/elements'
import { useCallback, useMemo } from 'react'

import type {
  BrandFormsMapType,
  FCFormBrandType,
} from 'components/Editor/EditorElements/elements.types'
import { Typography } from 'components/uiKit/Typography'
import { FONT_FIELDS } from 'services/Branding/fieldConfigs'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import {
  BrandFormFontList,
  BrandFormFontBase,
  BrandFormFontQuote,
  BrandFormFontLink,
  BrandGoBack,
} from '..'

const TypographyBase: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.base}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.base')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyH1: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.h1}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.h1')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyH2: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.h2}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.h2')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyH3: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.h3}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.h3')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyH4: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.h4}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.h4')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyCaption: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.caption}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.caption')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyList: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontList font={fontSchema.list}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.list')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontList>
  )
}

const TypographyQuote: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontQuote font={fontSchema.quote}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.quote')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontQuote>
  )
}

const TypographyLink: FCFormBrandType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
> = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontLink font={fontSchema.link}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('entity.font.link')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontLink>
  )
}

export default {
  base: TypographyBase,
  h1: TypographyH1,
  h2: TypographyH2,
  h3: TypographyH3,
  h4: TypographyH4,
  caption: TypographyCaption,
  list: TypographyList,
  quote: TypographyQuote,
  link: TypographyLink,
} satisfies BrandFormsMapType<
  ElementValue,
  ElementSchemaType,
  RichTextFontSchemaType,
  ElementSchemaType
>
